<script setup>
import { computed, ref, watch } from "vue";
import apiClient from "@/apiClient.js";
import { currentVatQuarter } from "@/utils/vatReturnPeriod";

const props = defineProps({ initialYear: { type: Number, required: true } });
const emit = defineEmits(["open"]);
const year = ref(props.initialYear);
const rows = ref([]);
const loading = ref(false);
const error = ref("");
let requestVersion = 0;
const money = (value) =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(
    value / 100,
  );
const quarters = computed(() => {
  const standard = Array.from({ length: 4 }, (_, index) => {
    const period = currentVatQuarter(
      `${year.value}-${String(index * 3 + 1).padStart(2, "0")}-01`,
    );
    const row = rows.value.find(
      ({ vat_return: item }) =>
        item.period_start === period.period_start &&
        item.period_end === period.period_end,
    );
    return { ...period, label: `Q${index + 1}`, ...row };
  });
  const other = rows.value.filter(
    ({ vat_return: item }) =>
      !standard.some(
        (period) =>
          period.period_start === item.period_start &&
          period.period_end === item.period_end,
      ),
  );
  return [
    ...standard,
    ...other.map((row) => ({
      ...row.vat_return,
      label: "Custom period",
      ...row,
    })),
  ];
});
const load = async () => {
  const version = ++requestVersion;
  if (
    !Number.isInteger(Number(year.value)) ||
    year.value < 2000 ||
    year.value > 2100
  ) {
    error.value = "Choose a year from 2000 to 2100.";
    rows.value = [];
    loading.value = false;
    return;
  }
  loading.value = true;
  error.value = "";
  rows.value = [];
  try {
    const { data } = await apiClient.get("/api/vat-returns/statistics", {
      params: { year: year.value },
    });
    if (version === requestVersion) rows.value = data;
  } catch (failure) {
    if (version === requestVersion)
      error.value =
        failure?.response?.data?.message ??
        "Unable to load quarterly VAT statistics.";
  } finally {
    if (version === requestVersion) loading.value = false;
  }
};
watch(year, load, { immediate: true });
</script>

<template>
  <form-box header="Quarterly VAT statistics">
    <div class="flex items-end gap-3">
      <label
        >Year<input
          v-model.number="year"
          type="number"
          min="2000"
          max="2100"
          aria-label="Statistics year"
          class="input block"
      /></label>
      <button type="button" class="btn" :disabled="loading" @click="load">
        Refresh
      </button>
    </div>
    <p class="my-3 text-sm text-slate-600 dark:text-slate-300">
      Unimported quarters show live estimates, including supplier VAT estimates
      awaiting evidence. Liability = output VAT − input VAT; a negative figure
      indicates a repayment. Imported quarters show preserved filed totals;
      other quarters show live estimates. Missing returns show —, rather than
      zero.
    </p>
    <p v-if="error" role="alert" class="text-rose-600">{{ error }}</p>
    <p v-else-if="loading" role="status">Loading quarterly statistics…</p>
    <div v-else class="overflow-x-auto">
      <table class="min-w-full text-sm">
        <thead class="text-left">
          <tr>
            <th class="p-2">Quarter / period</th>
            <th class="p-2">Status</th>
            <th class="p-2 text-right">Input VAT</th>
            <th class="p-2 text-right">Output VAT</th>
            <th class="p-2 text-right">Liability / (repayment)</th>
            <th class="p-2">Outstanding review</th>
            <th class="p-2">Return</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in quarters"
            :key="`${row.period_start}-${row.period_end}`"
            class="border-t border-slate-200 dark:border-slate-700"
          >
            <td class="p-2">
              {{ row.label }} · {{ row.period_start }} – {{ row.period_end }}
            </td>
            <td class="p-2">
              {{ row.vat_return?.status || "Not recorded"
              }}<span v-if="row.vat_return?.filed_import" class="block text-xs"
                >Filed workbook totals</span
              >
            </td>
            <td class="p-2 text-right">
              {{
                row.summary
                  ? money(
                      row.vat_return?.filed_import?.input_vat ??
                        row.summary.input_vat,
                    )
                  : "—"
              }}
            </td>
            <td class="p-2 text-right">
              {{
                row.summary
                  ? money(
                      row.vat_return?.filed_import?.output_vat ??
                        row.summary.output_vat,
                    )
                  : "—"
              }}
            </td>
            <td class="p-2 text-right">
              {{
                row.summary
                  ? money(
                      row.vat_return?.filed_import?.liability ?? row.liability,
                    )
                  : "—"
              }}
            </td>
            <td class="p-2">
              <template v-if="row.summary"
                >{{ row.summary.unmatched_transactions }} outgoing ·
                {{ row.summary.unmatched_income_transactions }} income<br />{{
                  money(row.summary.estimated_input_vat)
                }}
                estimated input VAT</template
              ><template v-else>—</template>
            </td>
            <td class="p-2">
              <button type="button" class="btn" @click="emit('open', row)">
                {{ row.vat_return ? "Open" : "Create draft" }}
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </form-box>
</template>
